<template>
  <v-autocomplete
    v-model="location"
    :items="searchResults.length > 0 ? searchResults : [{ _template: true }]"
    :loading="isLoading"
    :search-input.sync="search"
    item-text="name"
    item-value="data"
    :label="label"
    :outlined="outlined"
    hide-no-data
    hide-selected
    :placeholder="placeholder"
    :prepend-icon="prependIcon"
    :hide-details="hideDetails"
    :rules="rules"
    return-object
    :filter="filter"
    :rounded="rounded"
    :hint="hint"
    :disabled="disabled"
    :item-disabled="(item) => item._template"
    :autofocus="autofocus"
    :prepend-inner-icon="prependInnerIcon"
  >
    <template v-if="hasGeolocation && allowGeolocation" v-slot:prepend-item>
      <div class="px-2 py-1">
        <v-btn
          v-if="!errorGeolocation"
          block
          class=""
          color="primary"
          depressed
          text
          small
          :text="loadingGeolocation"
          :loading="loadingGeolocation"
          @click="getGeolocation"
        >
          <v-icon left>mdi-map-marker</v-icon>
          Usar minha localização
        </v-btn>
        <v-alert v-if="errorGeolocation" type="error" class="mb-0" dense text>
          {{ errorGeolocation }}
        </v-alert>
      </div>
    </template>
    <template v-slot:item="{ item }">
      <v-list-item-content v-if="!item._template">
        <v-list-item-title v-html="item.name"></v-list-item-title>
        <v-list-item-subtitle
          v-if="!hiddenItemsDetails"
          v-html="item.description"
        ></v-list-item-subtitle>
      </v-list-item-content>
      <div v-else>
        <v-list-item-title> {{ searchHint }} </v-list-item-title>
        <!-- <v-list-item-subtitle> {{ searchHint }} </v-list-item-subtitle> -->
      </div>
    </template>

    <template v-slot:selection="{ item }">
      {{ item.name }}
      <small v-if="!hiddenItemDetails" class="text--secondary ml-2">{{
        item.description
      }}</small>
    </template>
  </v-autocomplete>
</template>

<script>
const { VUE_APP_MAPS_KEY } = process.env;
import { Loader } from "@googlemaps/js-api-loader";
import { add } from "lodash";

const stateAbbreviations = {
  Acre: "AC",
  Alagoas: "AL",
  Amapá: "AP",
  Amazonas: "AM",
  Bahia: "BA",
  Ceará: "CE",
  "Distrito Federal": "DF",
  "Espírito Santo": "ES",
  Goiás: "GO",
  Maranhão: "MA",
  "Mato Grosso": "MT",
  "Mato Grosso do Sul": "MS",
  "Minas Gerais": "MG",
  Pará: "PA",
  Paraíba: "PB",
  Paraná: "PR",
  Pernambuco: "PE",
  Piauí: "PI",
  "Rio de Janeiro": "RJ",
  "Rio Grande do Norte": "RN",
  "Rio Grande do Sul": "RS",
  Rondônia: "RO",
  Roraima: "RR",
  "Santa Catarina": "SC",
  "São Paulo": "SP",
  Sergipe: "SE",
  Tocantins: "TO",
};

export default {
  data: () => ({
    location: null,
    isLoading: false,
    searchResults: [],
    search: "",
    autocomplete: null, // will reveal this later 🕵️
    timeout: null,

    // Google Maps API
    autocomplete: null,
    places: null,
    hasGeolocation: false,
    loadingGeolocation: false,
    errorGeolocation: null,
  }),
  props: {
    hiddenItemDetails: {
      type: Boolean,
      default: false,
    },
    hiddenItemsDetails: {
      type: Boolean,
      default: false,
    },
    searchTypes: {
      type: Array,
      default: () => [],
    },
    detailsFields: {
      type: Array,
      default: () => [],
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Busque por um local ou endereço",
    },
    minLength: {
      type: Number,
      default: 3,
    },
    delay: {
      type: Number,
      default: 500,
    },
    prependIcon: {
      type: String,
      default: null,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    hint: {
      type: String,
      default: undefined,
    },
    hideDetails: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prependInnerIcon: {
      type: String,
      default: null,
    },
    searchHint: {
      type: String,
      default: "Digite para pesquisar um local ou endereço",
    },
    allowGeolocation: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    async init() {
      this.hasGeolocation = !!navigator.geolocation;

      const loader = new Loader({
        apiKey: VUE_APP_MAPS_KEY,
        version: "weekly",
        libraries: ["places"],
      });
      const google = await loader.load();
      this.autocomplete = new google.maps.places.AutocompleteService();
      this.places = new google.maps.places.PlacesService(
        new google.maps.Map(document.createElement("div"), {})
      );
    },
    formatAddress(data) {
      return {
        name: data.name,
        street: this.findAddressComponent(data.address_components, "route")
          ?.long_name,
        number: this.findAddressComponent(
          data.address_components,
          "street_number"
        )?.long_name,
        complement: this.findAddressComponent(
          data.address_components,
          "subpremise"
        )?.long_name,
        neighborhood: this.findAddressComponent(
          data.address_components,
          "sublocality"
        )?.long_name,
        city: this.findAddressComponent(
          data.address_components,
          "administrative_area_level_2"
        )?.long_name,
        state: this.findAddressComponent(
          data.address_components,
          "administrative_area_level_1"
        )?.short_name,
        country: this.findAddressComponent(data.address_components, "country")
          ?.long_name,
        zipCode: this.findAddressComponent(
          data.address_components,
          "postal_code"
        )?.long_name,
        lat: data.geometry.location.lat(),
        lng: data.geometry.location.lng(),
      };
    },
    findAddressComponent(data, componentName) {
      return data.find((item) => item.types.includes(componentName));
    },
    runSearch(val) {
      this.autocomplete.getPlacePredictions(
        {
          input: val,
          types: this.searchTypes,
          language: "pt-BR",

          componentRestrictions: {
            country: "br",
          },
        },
        this.displaySuggestions
      );
    },
    filter(item, queryText, itemText) {
      return true;
    },
    addressDetails(place) {
      this.$emit("input:raw", place);
      this.$emit("input", this.formatAddress(place));
    },
    displaySuggestions(predictions, status) {
      this.isLoading = false;
      if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
        return (this.searchResults = []);
      }

      this.searchResults = predictions.map((prediction) => {
        return {
          name: prediction.structured_formatting.main_text,
          description: prediction.structured_formatting.secondary_text,
          placeId: prediction.place_id,
        };
      });
    },

    async getGeolocation() {
      try {
        this.loadingGeolocation = true;
        const position = await this.getCurrentPosition();
        const { latitude, longitude } = position.coords;
        const address = await this.getCityFromCoords(latitude, longitude);
        this.searchResults = [
          { name: address.city, description: address.state, _raw: address },
        ];
        this.location = this.searchResults[0];
        this.$emit("input", {
          ...address,
          name: `${address.city}, ${address.state}`,
        });
      } catch (error) {
        console.error(error);
        const code = error.code;
        if (code === 1) this.errorGeolocation = "Permissão negada";
        if (code === 2) this.errorGeolocation = "Posição não encontrada";
        if (code === 3) this.errorGeolocation = "Tempo excedido";
        this.errorGeolocation = "Erro ao obter geolocalização";
      } finally {
        this.loadingGeolocation = false;
      }
    },
    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
    },
    async getCityFromCoords(lat, lon) {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
      );
      const data = await response.json();
      return {
        ...data.address,
        name: `${data.address.city}, ${stateAbbreviations[data.address.state]}`,
        state: stateAbbreviations[data.address.state],
      };
    },
  },
  mounted() {
    this.init();
    if (this.value) {
      this.location = this.value;
      this.searchResults = [this.value];
    }
  },
  watch: {
    location(newValue) {
      debugger;
      if (!newValue) return this.$emit("input", null);
      if (newValue._raw) this.$emit("input", newValue._raw);
      if (!newValue.placeId) return;
      this.places.getDetails(
        {
          language: "pt-BR",
          placeId: newValue.placeId,
          fields: [
            "address_component",
            "name",
            "geometry",
            ...this.detailsFields,
          ],
        },
        this.addressDetails
      );
    },
    search(val) {
      if (!val || val.trim().length < this.minLength) return;
      if (this.timeout) clearTimeout(this.timeout);
      this.isLoading = true;
      this.timeout = setTimeout(() => this.runSearch(val), this.delay);
    },
  },
};
</script>

<style></style>
